import React, { useState, useEffect } from "react";
import { Button, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Typography, Chip, SvgIcon, Alert, Modal, Box} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Done as DoneIcon, Clear as ClearIcon, Event as EventIcon, History as PastIcon, ArrowForward as FutureIcon,
} from "@mui/icons-material";
import { SessionInfo } from "../../data/SessionData";
import { deleteUserSession, updateUserSession, } from "../../services/userSessionInformation";
import { useGlobal } from "../../utils/GlobalContext";
import "./SessionListPage.css";
import EditUserSessionModal from "./EditUserSessionModal";
import { getEventById } from "../../services/cdn/Events";
import { EventsListItem } from "../../data/eventInfo";
import { Session } from "inspector";
import { getEventInfo } from "../../services/EventInformation";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import CircularProgress from '@mui/material/CircularProgress';

interface SessionListProps {
  userSessions: SessionInfo[];
  isLoading: boolean;
  userId: String;
  events: EventsListItem[];
  onUserSessionUpdate: () => void;
}

export default function SessionListPage({ userSessions, isLoading, userId, events, onUserSessionUpdate }: SessionListProps) {
  const { state, dispatch } = useGlobal();
  const [sessions, setSessions] = useState<SessionInfo[]>(userSessions);
  const [filter, setFilter] = useState("all");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const CurrentId = String(userId);
  const [isLoad, setIsSessionsLoading] = useState(isLoading); // Loading state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState<SessionInfo | null>(null);
  const [newUserSession, setNewUserSession] = useState<SessionInfo | null>(null);
  const [isShowAlert, setShowAlert] = useState(false); //Success Alert
  const [errorMessage, setErrorMessage] = useState(false); //Error Alert
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<SessionInfo | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventsListItem | null>(null);


  useEffect(() => {
    setSessions(userSessions);
  }, [userSessions]);

  useEffect(() => {
    setIsSessionsLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleDeleteSession = async (session: SessionInfo) => {
    if (session.IsSelected) {
      window.alert(
        "This session has already been approved and cannot be deleted."
      );
      return;
    }

    try {
      const sessionWithUserId = { ...session, UserId: CurrentId };

      const response = await deleteUserSession(sessionWithUserId, CurrentId);
      console.log(response);
      setSessions((currentSessions: any[]) =>
        currentSessions.filter((s) => s.Id !== session.Id)
      );
    } catch (error) {
      console.error("Failed to delete session:", error);
    }
  };

  const handleEditSession = (session: SessionInfo) => {
    if (session.IsSelected || session.SessionEventStatus === 3) {
      window.alert("This session cannot be edited because it has been approved, or is in the past/present.");
      return;
    }

    setSessionToEdit(session);
    setEditModalOpen(true);
  };

  const handleSaveSession = async (updatedSession: SessionInfo) => {
    console.log("Updated session:", updatedSession);

    try {
      const sessionWithUserId = { ...updatedSession, UserId: CurrentId };
      const response = await updateUserSession(sessionWithUserId, CurrentId);
      console.log(response);
      setSessions((currentSessions: SessionInfo[]) => {
        return currentSessions.map((session) => {
          if (session.Id === updatedSession.Id) {
            // Replace the session with the updated one
            return updatedSession;
          }
          return session;
        });
      });
      if (updatedSession.Id === undefined || updatedSession.Id === null) {
        onUserSessionUpdate();
      }
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
      }, 5000);
    } catch (error) {
      setErrorMessage(true);
      setTimeout(() => {
        setShowAlert(false);
      }, 5000);
      console.error("Failed to update session:", error);
    }
  };

  const getSessionStatusDetails = (isSelected: boolean) => {
    if (isSelected) {
      return windowWidth < 800
        ? { Icon: DoneIcon, backgroundColor: "green", color: "white" }
        : {
          label: "Accepted",
          Icon: DoneIcon,
          backgroundColor: "green",
          color: "white",
        };
    } else {
      return windowWidth < 800
        ? { Icon: ClearIcon, backgroundColor: "red", color: "white" }
        : {
          label: "Not Approved",
          Icon: ClearIcon,
          backgroundColor: "red",
          color: "white",
        };
    }
  };

  const getSessionIcon = (session: SessionInfo) => {
    if (session.SessionEventStatus === 3) {
      return windowWidth < 800
        ? { Icon: PastIcon, backgroundColor: "#e0e0e0" }
        : { label: "Past", Icon: PastIcon, backgroundColor: "#e0e0e0" };
    } else if (session.SessionEventStatus === 1) {
      return windowWidth < 800
        ? { Icon: FutureIcon, backgroundColor: "#2196f3" }
        : { label: "Future", Icon: FutureIcon, backgroundColor: "#2196f3" };
    } else {
      return windowWidth < 800
        ? { Icon: EventIcon, backgroundColor: "#4caf50" }
        : { label: "Present", Icon: EventIcon, backgroundColor: "#4caf50" };
    }
  };

  const filteredSessions = sessions.filter((session) => {
    if (filter === "past") {
      return session.SessionEventStatus === 3;
    } else if (filter === "future") {
      return session.SessionEventStatus === 1;
    } else if (filter === "present") {
      return session.SessionEventStatus === 2;
    } else {
      return true;
    }
  }).sort((a, b) => {
    if (filter === "all") {
      return a.SessionEventStatus - b.SessionEventStatus;
    }
    return 0; // No sorting for other filters
  });

  const [eventInfo, setEventInfo] = useState<EventsListItem[]>([]);
  const [eventList, setEventList] = useState<EventsListItem[]>([]);
  useEffect(() => {
    const fetchEventInfo = async () => {
      try {
        setEventList(events);
        if (events) {
          const filteredEvents = events.filter(
            (event) => parseInt(event.Status) === 1 || parseInt(event.Status) === 2
          );
          
          setEventInfo(filteredEvents);
        }
      } catch (error) {
        console.error("Failed to fetch event information:", error);
      }
    };
  
    fetchEventInfo();
  }, [events]); // Dependency on eventIds so that the effect runs when eventIds change

  const handleAddSession = () => {
    setNewUserSession((prevState) => {
      const updatedUserSession = {
        ...prevState!,
        UserId: CurrentId,
      };
      setSessionToEdit(updatedUserSession);
      setEditModalOpen(true);
      return updatedUserSession;
    });
};

const handleListItemClick = async (session: SessionInfo) => {
  setSelectedSession(session);
  try {
    const event = await getEventById(session.EventId); 
    setSelectedEvent(event);
    setModalOpen(true);
  } catch (error) {
    console.error("Failed to fetch event details:", error);
  }
};

const handleCloseModal = () => {
  setModalOpen(false);
  setSelectedSession(null);
};

  return (
    <div className="session-list-page">
      <div className="filter-buttons">
        <div>
          {isShowAlert && (
            <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
              User Session successfully submitted.
            </Alert>
          )}
          {errorMessage && (
            <Alert icon={<ErrorIcon fontSize="inherit" />} severity="error">
              Error saving User Session.
            </Alert>
          )}
        </div>
        {["all", "past", "present", "future"].map((f) => (
          <Button
            key={f}
            variant={filter === f ? "contained" : "outlined"}
            onClick={() => setFilter(f)}
            style={{ marginRight: "8px" }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>
      <Button
        variant="contained"
        color="primary"
        style={{ marginBottom: "20px" }}
        onClick={handleAddSession}
      >
        Add New Session
      </Button>
      {isLoading ? (
      <Box sx={{ display: 'flex', alignContent:"center" }}>
        <CircularProgress color="info"/>
      </Box>    
    ) : sessions.length > 0 ? (
        <List>
          {filteredSessions.map((session) => {
            const {
              label: SessionLabel,
              Icon: SessionIcon,
              backgroundColor: SessionBackground,
            } = getSessionIcon(session);
            const {
              label: StatusLabel,
              Icon: StatusIcon,
              backgroundColor: StatusBackground,
            } = getSessionStatusDetails(session.IsSelected);
            const associatedEvent = eventList.find(event => event.Id === session.EventId);
            return (
              <ListItem key={session.Id} divider>
                <div className="list-item">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      maxWidth: windowWidth >= 800 ? "100%" : "80%",
                    }}
                  >
                    <ListItemText
                      primary={session.Title}
                      className="list-item-text"
                      onClick={() => handleListItemClick(session)}
                    />
                    <div className="session-chip">
                      {windowWidth >= 800 ? (
                        <Chip
                          icon={SessionIcon ? <SessionIcon /> : undefined}
                          label={
                            <Typography style={{ color: "white" }}>
                              {SessionLabel}
                            </Typography> 
                          }       
                          style={{ backgroundColor: SessionBackground }}
                        />  
                             
                      ) : (
                        
                        <div>
                          
                          <SvgIcon
                            component={SessionIcon}
                            className="session-svg-icon"
                            style={{
                              backgroundColor: SessionBackground,
                              height: "40px",
                              width: "40px",
                              padding: "7px",
                            }}
                          />
                        </div>
                      )}  
                          
                  </div>
                  <div className="event-title">{associatedEvent?.Name}</div>
                  </div>
                  <ListItemSecondaryAction>
                    <div className="list-item-secondary-action">
                      {windowWidth >= 800 ? (
                        <Chip
                          icon={StatusIcon ? <StatusIcon /> : undefined}
                          label={
                            <Typography style={{ color: "white" }}>
                              {StatusLabel}
                            </Typography>
                          }
                          style={{ backgroundColor: StatusBackground }}
                        />
                      ) : (
                        <div>
                          <SvgIcon
                            component={StatusIcon}
                            className="status-svg-icon"
                            style={{
                              backgroundColor: StatusBackground,
                              height: "40px",
                              width: "40px",
                              padding: "7px",
                            }}
                          />
                        </div>
                      )}
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => handleEditSession(session)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteSession(session)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </ListItemSecondaryAction>
                </div>
              </ListItem>
            );
          })}
        </List>
      ) : (
        <p>You currently do not have any sessions.</p>
      )}
      {editModalOpen && sessionToEdit && (
        <EditUserSessionModal
          session={sessionToEdit}
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleSaveSession}
          events={eventInfo}
        />
      )}
      <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          aria-labelledby="session-modal-title"
          aria-describedby="session-modal-description"
        >
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, width:'50%', height: '50%', overflowY: 'auto' }}>
              <Typography id="session-modal-title" variant="h6" component="h2" sx={{fontWeight: "bold", marginBottom: "10px", textAlign: "center"}}>
                {selectedSession && selectedSession.Title}
              </Typography>
              <Typography id="session-modal-event-title" sx={{marginTop: '10px', marginBottom: '10px'}}>
                <b>Event: </b> {selectedEvent?.Name}
              </Typography>
              <Typography id="session-modal-description" sx={{marginTop: '10px', marginBottom: '10px'}}>
                <b>Description: </b> {selectedSession?.Description}
              </Typography>
              <Typography id="session-modal-workshop">
                <b>IsWorkshop: </b>{selectedSession?.IsWorkshop === false ? "No" : "Yes"}
              </Typography>
            </Box>
        </Modal>
    </div>
  );
}